import { BadgeListProps } from '@aboutyou/legacy-shared/src/ui/BadgeList/types'
import { exists } from 'helpers/exists'
import { applyProductModifiers, ProductModifiers } from 'helpers/products'
import { TranslateFnType } from 'store'
import Product from 'store/entities/Product/Product.store'

export const getProductTileBadgesList = (
  product: Product,
  t: TranslateFnType,
  {
    isSale,
    isCampaign,
    formattedReduction,
    formattedCampaignReduction,
    campaignColor,
    likedBrandIds,
    basketProductIds,
    displayMode,
    isDisplayedInProductTile,
    productIsSponsored,
    isSimilarProduct,
    showSecondHand,
    showSustainableBadge = true,
    productModifiers,
    shouldCombineSaleAndCampaignBadges,
    badgeDealTranslation,
  }: {
    isSale: boolean
    isCampaign: boolean
    productIsSponsored: boolean
    isSimilarProduct?: boolean
    formattedCampaignReduction?: string
    formattedReduction?: string
    campaignColor?: string
    likedBrandIds?: Set<string>
    basketProductIds?: Set<string>
    displayMode: BadgeListProps['displayMode']
    isDisplayedInProductTile: BadgeListProps['isDisplayedInProductTile']
    showSecondHand: boolean
    showSustainableBadge?: boolean
    productModifiers?: ProductModifiers
    shouldCombineSaleAndCampaignBadges: boolean
    badgeDealTranslation?: (reductionPercentage: string) => string
  },
): BadgeListProps => {
  const productModifiedProps = applyProductModifiers(product, productModifiers)

  const isFromLikedBrand =
    exists(likedBrandIds) &&
    exists(product.brandDetails.id) &&
    likedBrandIds.has(product.brandDetails.id)

  const isInBasket =
    exists(basketProductIds) && basketProductIds.has(product.id)

  const saleText = formattedReduction || t('Sale')
  const campaignText = formattedCampaignReduction || t('Extra')

  return {
    shouldCombineSaleAndCampaignBadges,
    isBreastFeedingCapable: Boolean(product.isBreastFeedingCapable),
    isExclusive: Boolean(product.isExclusive),
    isKids: Boolean(product.isKids),
    isNew: Boolean(product.isNew),
    isPremium: Boolean(product.isPremium),
    isSimilar: Boolean(isSimilarProduct),
    isSponsored: productIsSponsored,
    isSustainable: Boolean(product.isSustainable && showSustainableBadge),
    pack: product.pack,
    personalised: isFromLikedBrand ? 'favorite' : undefined,
    sale: {
      campaignBackgroundColor: campaignColor,
      campaignSaleText: isCampaign ? campaignText : undefined,
      saleText: isSale ? saleText : undefined,
    },
    specialSizes: product.isSpecialSize ?? noSpecialSizesFallback,
    translations: {
      new: t('Neu'),
      kids: t('Kinder'),
      premium: t('Premium'),
      sponsored: t('Tipp'),
      sustainable: t('Nachhaltiger'),
      breastFeedingCapable: t('Mit Stillfunktion'),
      similar: t('Ähnlich'),
      exclusive: t('Exklusiv'),
      inBasket: t('Im Warenkorb'),
      specialSizes: {
        mom: t('Mama'),
        petite: t('Petite'),
        tall: t('Tall'),
        plusSizeMale: t('Big & Tall'),
        plusSizeFemale: t('Curvy'),
      },
      personalized: {
        favorite: t('Deine Lieblingsmarke'),
        oftenBought: t('Häufig gekauft'),
        recommended: t('Unsere Empfehlung'),
      },
      secondHand: t('Gebraucht'),
      exaDrop: t('Drop'),
      sneakerReleaseCalender: t('Sneaker Release Calendar'),
      secondLove: t('Second Love'),
      upcycled: t('Upcycled Fashion'),
      unique: t('Unikat'),
      deal: badgeDealTranslation,
    },
    productIsInBasket: isInBasket,
    displayMode,
    isDisplayedInProductTile,
    isSecondHand: Boolean(product.isSecondHand),
    showSecondHand,
    isExaDrop: Boolean(product.isExaDrop),
    isSneakerReleaseCalenderDrop: Boolean(product.isSneakerReleaseCalenderDrop),
    brandHandPicked: product.brandHandPicked || '',
    isSecondLove:
      productModifiedProps?.isSecondLove !== undefined
        ? productModifiedProps.isSecondLove
        : Boolean(product.isSecondLove),
    isUpcycled: Boolean(product.isUpcycled),
    isUnique: Boolean(product.isUpcycled),
  }
}

const noSpecialSizesFallback = {
  isForMom: false,
  isForPetite: false,
  isForTall: false,
  isPlusSizeMale: false,
  isPlusSizeFemale: false,
}
